
import React from 'react';
import { useProviderForm } from './form/hooks/useProviderForm';
import { EssentialInformation } from './form/EssentialInformation';
import { ServiceCapabilities } from './form/ServiceCapabilities';
import { VerifiedCertifications } from './form/VerifiedCertifications';
import { VerifiedInformation } from './form/VerifiedInformation';
import { CarveOutSettings } from './form/CarveOutSettings';
import { PricingInformationSection } from './form/sections/PricingInformationSection';
import { PerformanceMetricsSection } from './form/sections/PerformanceMetricsSection';
import { ServiceDeliverySection } from './form/sections/ServiceDeliverySection';
import { FormActions } from './form/FormActions';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Form } from '@/components/ui/form';
import type { PEOProvider } from '@/types/peo.types';

export interface PEOProviderFormProps {
  onClose: () => void;
  onSuccess: () => void;
  provider?: PEOProvider;
  onDeleteProvider?: (providerId: string) => Promise<boolean>;
  isDeleteInProgress?: boolean;
}

export const PEOProviderForm: React.FC<PEOProviderFormProps> = ({ 
  onClose, 
  onSuccess,
  provider,
  onDeleteProvider,
  isDeleteInProgress = false
}) => {
  console.log('PEOProviderForm render:', { 
    provider: provider?.name, 
    isDeleteInProgress,
    hasDeleteHandler: !!onDeleteProvider 
  });

  const {
    form,
    register,
    control,
    errors,
    handleSubmit,
    onSubmit,
    handleDelete,
    setValue,
    getValues,
    watch,
    isSubmitting,
    isDeleteInProgress: formDeleteInProgress
  } = useProviderForm(
    provider,
    onSuccess,
    onDeleteProvider,
    isDeleteInProgress
  );

  const actualDeleteInProgress = isDeleteInProgress || formDeleteInProgress;

  const handleFormSubmit = async (data: any) => {
    console.log('Form submission started with data:', data);
    const result = await onSubmit(data);
    console.log('Form submission completed:', result);
    return result;
  };

  const handleFormDelete = async () => {
    console.log('Form deletion started for provider:', provider?.name);
    await handleDelete();
    console.log('Form deletion completed');
  };

  return (
    <div className="flex flex-col h-full max-h-[80vh]">
      <ScrollArea className="flex-1 px-6">
        <Form {...form}>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8 pb-6">
          <div className="space-y-6">
            <EssentialInformation 
              register={register} 
              errors={errors}
              setValue={setValue}
              watch={watch}
            />
            
            <Separator />
            
            <VerifiedInformation 
              register={register} 
              setValue={setValue}
              getValues={getValues}
            />
            
            <Separator />
            
            <VerifiedCertifications 
              register={register} 
              setValue={setValue}
              getValues={getValues}
            />
            
            <Separator />
            
            <ServiceCapabilities 
              register={register} 
              setValue={setValue}
              getValues={getValues}
            />
            
            <Separator />
            
            <CarveOutSettings 
              register={register} 
              setValue={setValue}
              getValues={getValues}
            />
            
            <Separator />
            
            <PricingInformationSection control={control} />
            
            <Separator />
            
            <ServiceDeliverySection control={control} />
            
            <Separator />
            
            <PerformanceMetricsSection control={control} />
          </div>
          
          <FormActions
            provider={provider}
            onDelete={handleFormDelete}
            isSubmitting={isSubmitting}
            isDeleteInProgress={actualDeleteInProgress}
          />
          </form>
        </Form>
      </ScrollArea>
    </div>
  );
};
